import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useGet } from "../api/BaseEntityApi";
import { SessionContext } from "../common/contexts/SessionContext";
import { DatasetDetail } from "./DatasetDetail";
import { Navigation } from "./Navigation/Navigation";
// import styles from "./Dataset.module.css";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import FullDetail from "../common/panels/FullDetail/FullDetail";
import { EntityDetailProps } from "../common/entity/EntityInterfaces";
import { DatasetDetailPageProps } from "./DatasetViews";
import { QueueJob } from "../api/Datasets";
import { renderError } from "../common/helperfunctions/ApiError";

export interface FullScreenOpts {
  state: boolean;
  component: "files" | "parameters" | "";
}

// The context shared among all child components for the Detail page
export interface Store {
  // A global full screen state
  fullscreen: {
    get: FullScreenOpts;
    set: React.Dispatch<React.SetStateAction<FullScreenOpts>>;
  };
  // A state wether to hide certain features for shared bundle view
  viewShared: boolean;
  mWebEnabled: boolean;
  viewViewer: boolean;
}
export const DatasetContext = createContext<Store | null>(null);

export const DatasetsDetail = ({
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  viewShared,
  viewViewer,
  entityTypeId,
}: EntityDetailProps<"datasets"> & DatasetDetailPageProps) => {
  // const history = useHistory();
  const queryClient = useQueryClient();
  const { api, session } = useContext(SessionContext);

  const [queueId, setQueueId] = useState<number>();
  const [fullscreen, setFullscreen] = useState<FullScreenOpts>({
    state: false,
    component: "",
  });
  const queuedDatasetRef = useRef<number | undefined>(undefined);

  const { mutateAsync: queueJobAsync, isLoading: isQueueingJob } = useMutation<QueueJob>(
    () => api.get(`${entityConstants.resource}/${entity.id}/high_priority`, {}),
    {
      onError: (err, variables) => {
        renderError(err);
      },
    }
  );

  const refetchDataset = useCallback(() => {
    if (queueId) {
      queryClient.invalidateQueries([entityConstants.resource, "detail", entity.id]); // Invalidation does not for useQueries with options {enabled : ...}
    }
  }, [entity.id, entityConstants.resource, queryClient, queueId]);

  useEffect(() => {
    if (!queueId) return;
    const intervalId = setInterval(refetchDataset, 3000);
    return () => clearInterval(intervalId);
  }, [queueId, refetchDataset]);

  useEffect(() => {
    const queueParsingJob = async () => {
      if (entity.parsingState !== "NotYetParsed") {
        setQueueId(undefined);
        queuedDatasetRef.current = undefined;
        return;
      }

      setQueueId(entity.id);

      if (queuedDatasetRef.current === entity.id || isQueueingJob) {
        return;
      }

      queuedDatasetRef.current = entity.id;
      await queueJobAsync().catch(() => {
        queuedDatasetRef.current = undefined;
      });
    };

    queueParsingJob();
  }, [entity.id, entity.parsingState, isQueueingJob, queueJobAsync]);

  useEffect(() => {
    if (entity.id !== queuedDatasetRef.current) {
      queuedDatasetRef.current = undefined;
    }
  }, [entity.id]);

  // Use an endpoint for the MWeb feature flag check in a shared bundle context
  const { data: enableMweb } = useGet<boolean>(`mweb/status`, undefined, { enabled: !!viewShared });

  const mWebEnabled: boolean = useMemo(
    () =>
      (enableMweb && entity && entity.format?.id === "multi_mestrelab_mnova") ||
      (!!session?.features.enable_mweb && entity.format?.id === "multi_mestrelab_mnova"),
    [enableMweb, entity, session?.features.enable_mweb]
  );

  const context: Store = useMemo(
    () => ({
      fullscreen: { get: fullscreen, set: setFullscreen },
      viewShared: !!viewShared,
      mWebEnabled: mWebEnabled,
      viewViewer: !!viewViewer,
    }),
    [fullscreen, mWebEnabled, viewShared, viewViewer]
  );

  return (
    <DatasetContext.Provider value={context}>
      {viewShared ? (
        <DatasetDetail api={api} entity={entity} viewViewer={!!viewViewer} />
      ) : (
        <FullDetail>
          <Navigation
            entityTypeId={entityTypeId}
            entity={entity}
            entityConstants={entityConstants}
            permissions={permissions}
            routes={routes}
            entityApi={entityApi}
            fieldLabels={fieldLabels}
            api={api}
            viewViewer={!!viewViewer}
          />
          <DatasetDetail api={api} entity={entity} viewViewer={!!viewViewer} />
        </FullDetail>
      )}
    </DatasetContext.Provider>
  );
};
